import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { ShoppingCart, Loader2 } from 'lucide-react'
import api from '@/services/api'

import formatQuadrado25x25 from '@/assets/format-quadrado-25x25.webp'
import formatQuadrado30x30 from '@/assets/format-quadrado-30x30.webp'
import formatRetrato23x31 from '@/assets/format-retrato-23x31.webp'
import formatPaisagem23x31 from '@/assets/format-paisagem-23x31.webp'
import formatPaisagem40x30 from '@/assets/format-paisagem-40x30.webp'

type Orientation = 'landscape' | 'portrait' | 'square'

interface Product {
  id: string
  name: string
  image: string
  basePrice: number
}

interface DynamicProduct {
  type: 'dynamic_products'
  id: string
  attributes: {
    width: number
    height: number
    value: string
    orientation?: Orientation
  }
}

interface DynamicLine {
  type: 'dynamic_lines'
  id: string
  attributes: {
    price: string
    number_pages: number
    maximum_photos: number
    dynamic_product_id: string
  }
}

interface RawAddon {
  id: string
  type: 'product_addon'
  attributes: {
    right_product_id: string
    original_name: string
    description: string
    original_price: string
    price: string
    thumbnail_url?: string
  }
}

interface Addon {
  id: string
  product_id: string
  name: string
  description: string
  original_price: number
  price: number
  thumbnail_url?: string
}

interface RawCuratory {
  id: string
  attributes: {
    value: number
    number_photos: number
  }
}

interface Curatory {
  id: string
  value: number
  number_photos: number
}

interface SubProduct {
  id: string
  horizontal: number | null
  vertical: number | null
  orientation: Orientation
  relatedLines: {
    id: string
    numberPages: number
    price: number
    maximumPhotos: number
  }[]
}

function computeOrientation(h?: number | null, v?: number | null): Orientation {
  if (!h || !v) return 'landscape'
  if (h > v) return 'landscape'
  if (h < v) return 'portrait'
  return 'square'
}

function getOrientationLabel(o: Orientation): string {
  if (o === 'landscape') return 'Paisagem'
  if (o === 'portrait') return 'Retrato'
  return 'Quadrado'
}

function getFormatImage(o: Orientation, h?: number, v?: number): string {
  if (o === 'square')
    return h && h >= 30 ? formatQuadrado30x30 : formatQuadrado25x25
  if (o === 'portrait') return formatRetrato23x31
  if (o === 'landscape')
    return h && h >= 40 ? formatPaisagem40x30 : formatPaisagem23x31
  return formatPaisagem23x31
}

interface Props {
  product: Product
  /** Called when the user selects a format (dynamic product). Used to switch gallery to that format's images when available. */
  onFormatChange?: (dynamicProductId: string) => void
}

const ProductCustomizationInlineAPI = ({
  product,
  onFormatChange,
}: Props) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [subProducts, setSubProducts] = useState<SubProduct[]>([])
  const [selectedSubProduct, setSelectedSubProduct] =
    useState<SubProduct | null>(null)
  const [selectedPageOption, setSelectedPageOption] = useState<{
    id: string
    numberPages: number
    price: number
    maximumPhotos: number
  } | null>(null)
  const [curatories, setCuratories] = useState<Curatory[]>([])
  const [selectedCuratory, setSelectedCuratory] = useState<Curatory | null>(
    null,
  )
  const [addons, setAddons] = useState<Addon[]>([])
  const [selectedAddons, setSelectedAddons] = useState<Addon[]>([])

  useEffect(() => {
    fetchProductDetails()
    fetchCuratories()
  }, [product.id])

  useEffect(() => {
    if (selectedSubProduct) {
      onFormatChange?.(selectedSubProduct.id)
    }
  }, [selectedSubProduct, onFormatChange])

  async function fetchProductDetails() {
    setIsLoading(true)
    const companyId = import.meta.env.VITE_COMPANY_ID ?? ''
    try {
      const res = await api.get<{
        data: any
        included?: (DynamicProduct | DynamicLine | RawAddon)[]
      }>(`/b2c/public/products/${product.id}`, {
        headers: { 'X-Current-Company-ID': companyId },
        params: {
          include: 'dynamic_products,dynamic_products.dynamic_lines,addons',
        },
      })
      const included = res.data.included || []

      const addonItems: Addon[] = included
        .filter((item): item is RawAddon => item.type === 'product_addon')
        .map(item => ({
          id: item.id,
          product_id: item.attributes.right_product_id,
          name: item.attributes.original_name,
          description: item.attributes.description,
          original_price: parseFloat(item.attributes.original_price),
          price: parseFloat(item.attributes.price),
          thumbnail_url: item.attributes.thumbnail_url,
        }))
      setAddons(addonItems)

      const subs: SubProduct[] = included
        .filter(
          (item): item is DynamicProduct => item.type === 'dynamic_products',
        )
        .map(item => {
          const relatedLines = included
            .filter(
              (line): line is DynamicLine =>
                line.type === 'dynamic_lines' &&
                line.attributes.dynamic_product_id === item.id,
            )
            .map(line => ({
              id: line.id,
              numberPages: line.attributes.number_pages,
              price: parseFloat(line.attributes.price),
              maximumPhotos: line.attributes.maximum_photos,
            }))

          const o: Orientation =
            item.attributes.orientation ||
            computeOrientation(item.attributes.width, item.attributes.height)

          return {
            id: item.id,
            horizontal: item.attributes.width,
            vertical: item.attributes.height,
            orientation: o,
            relatedLines,
          }
        })

      setSubProducts(subs)
    } catch (error) {
      console.error('Erro ao buscar detalhes do produto:', error)
    } finally {
      setIsLoading(false)
    }
  }

  async function fetchCuratories() {
    const companyId = import.meta.env.VITE_COMPANY_ID ?? ''
    try {
      const res = await api.get<{ data: RawCuratory[] }>(
        '/b2c/public/curatories',
        {
          headers: { 'X-Current-Company-ID': companyId },
          params: {
            columnOrdering: 'value',
            ordination: 'desc',
            per_page: 'all',
          },
        },
      )
      setCuratories(
        res.data.data.map(item => ({
          id: item.id,
          value: item.attributes.value,
          number_photos: item.attributes.number_photos,
        })),
      )
    } catch {
      setCuratories([])
    }
  }

  const handleAddonToggle = (addon: Addon) => {
    setSelectedAddons(prev =>
      prev.some(a => a.id === addon.id)
        ? prev.filter(a => a.id !== addon.id)
        : [...prev, addon],
    )
  }

  const getTotalPrice = () => {
    const productPrice = selectedPageOption?.price || 0
    const curatoryPrice = selectedCuratory?.value || 0
    const addonsPrice = selectedAddons.reduce((sum, a) => sum + a.price, 0)
    return Number(productPrice) + Number(curatoryPrice) + Number(addonsPrice)
  }

  const handleProceedToCheckout = () => {
    if (!selectedSubProduct || !selectedPageOption) return
    navigate('/checkout', {
      state: {
        productId: product.id,
        productName: product.name,
        productImage: product.image,
        orientation: getOrientationLabel(selectedSubProduct.orientation),
        pages: selectedPageOption.numberPages,
        price: getTotalPrice(),
        coverType: undefined,
        selectedSubProduct,
        selectedPageOption,
        selectedCuratory,
        selectedAddons,
      },
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-8 bg-[#f7f3ee] border border-border rounded-2xl p-6 md:p-8">
      <div>
        <h2 className="text-2xl font-serif font-bold mb-2">
          Personalize seu {product.name}
        </h2>
        <p className="text-base text-muted-foreground mb-6">
          Escolha o formato e quantidade de páginas para criar seu fotolivro
          perfeito
        </p>
      </div>

      {/* Formato */}
      <div className="space-y-3">
        <Label className="text-lg font-semibold">Escolha o formato:</Label>
        <RadioGroup
          value={selectedSubProduct?.id}
          onValueChange={value => {
            const sub = subProducts.find(s => s.id === value)
            if (sub) {
              setSelectedSubProduct(sub)
              setSelectedPageOption(null)
            }
          }}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {subProducts.map(sub => (
              <div
                key={sub.id}
                role="button"
                tabIndex={0}
                className={`relative border rounded-lg p-3 cursor-pointer transition-all ${
                  selectedSubProduct?.id === sub.id
                    ? 'border-primary border-2 bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => {
                  setSelectedSubProduct(sub)
                  setSelectedPageOption(null)
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedSubProduct(sub)
                    setSelectedPageOption(null)
                  }
                }}
              >
                <div className="flex flex-col items-center space-y-2">
                  <div className="w-full aspect-square overflow-hidden rounded-md bg-muted">
                    <img
                      src={getFormatImage(
                        sub.orientation,
                        sub.horizontal || undefined,
                        sub.vertical || undefined,
                      )}
                      alt={getOrientationLabel(sub.orientation)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex items-center space-x-2 w-full justify-center">
                    <RadioGroupItem value={sub.id} id={`format-${sub.id}`} />
                    <Label
                      htmlFor={`format-${sub.id}`}
                      className="cursor-pointer text-center"
                    >
                      <div className="font-semibold">
                        {getOrientationLabel(sub.orientation)}
                      </div>
                      {sub.horizontal && sub.vertical && (
                        <div className="text-sm text-muted-foreground">
                          {sub.horizontal}x{sub.vertical} cm
                        </div>
                      )}
                    </Label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </RadioGroup>
      </div>

      {/* Páginas */}
      {selectedSubProduct && selectedSubProduct.relatedLines.length > 0 && (
        <div className="space-y-3">
          <Label className="text-lg font-semibold">Número de Páginas:</Label>
          <RadioGroup
            value={selectedPageOption?.id}
            onValueChange={value => {
              const line = selectedSubProduct.relatedLines.find(
                l => l.id === value,
              )
              if (line) setSelectedPageOption(line)
            }}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {selectedSubProduct.relatedLines.map(line => (
                <div
                  key={line.id}
                  role="button"
                  tabIndex={0}
                  className={`flex items-center space-x-2 border rounded-lg p-3 cursor-pointer transition-all ${
                    selectedPageOption?.id === line.id
                      ? 'border-primary border-2 bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedPageOption(line)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setSelectedPageOption(line)
                    }
                  }}
                >
                  <RadioGroupItem value={line.id} id={`pages-${line.id}`} />
                  <Label
                    htmlFor={`pages-${line.id}`}
                    className="cursor-pointer flex-1"
                  >
                    <span className="font-semibold block">
                      {line.numberPages} páginas
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {line.maximumPhotos} fotos
                    </span>
                  </Label>
                </div>
              ))}
            </div>
          </RadioGroup>
        </div>
      )}

      {/* Curadoria */}
      {curatories.length > 0 && (
        <div className="space-y-3">
          <Label className="text-lg font-semibold">
            Deseja adicionar Curadoria de Fotos?
          </Label>
          <RadioGroup
            value={selectedCuratory?.id || 'none'}
            onValueChange={value => {
              if (value === 'none') setSelectedCuratory(null)
              else {
                const cur = curatories.find(c => c.id === value)
                if (cur) setSelectedCuratory(cur)
              }
            }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div
                role="button"
                tabIndex={0}
                className={`flex items-center space-x-2 border rounded-lg p-3 cursor-pointer transition-all ${
                  !selectedCuratory
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => setSelectedCuratory(null)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedCuratory(null)
                  }
                }}
              >
                <RadioGroupItem value="none" id="curation-none" />
                <Label
                  htmlFor="curation-none"
                  className="cursor-pointer font-semibold flex-1"
                >
                  Não adicionar
                </Label>
              </div>
              {curatories.map(cur => (
                <div
                  key={cur.id}
                  role="button"
                  tabIndex={0}
                  className={`flex items-center space-x-2 border rounded-lg p-3 cursor-pointer transition-all ${
                    selectedCuratory?.id === cur.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedCuratory(cur)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setSelectedCuratory(cur)
                    }
                  }}
                >
                  <RadioGroupItem value={cur.id} id={`curation-${cur.id}`} />
                  <Label
                    htmlFor={`curation-${cur.id}`}
                    className="cursor-pointer flex-1"
                  >
                    <span className="font-semibold block">
                      Até {cur.number_photos} fotos
                    </span>
                    <span className="text-base text-muted-foreground">
                      {cur.value > 0
                        ? `(+ R$ ${cur.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`
                        : '(Grátis)'}
                    </span>
                  </Label>
                </div>
              ))}
            </div>
          </RadioGroup>
        </div>
      )}

      {/* Addons */}
      {addons.length > 0 && (
        <div className="space-y-3">
          <Label className="text-lg font-semibold">
            Monte seu pacote e economize!
          </Label>
          <div className="grid gap-3">
            {addons.map(addon => {
              const isSelected = selectedAddons.some(a => a.id === addon.id)
              const discount = addon.original_price - addon.price
              const discountPct = Math.round(
                (discount / addon.original_price) * 100,
              )
              return (
                <label
                  key={addon.id}
                  className={`cursor-pointer rounded-lg border-2 transition-all duration-300 ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={isSelected}
                    onChange={() => handleAddonToggle(addon)}
                  />
                  <div className="flex items-center gap-4 p-4">
                    {addon.thumbnail_url && (
                      <div className="w-16 h-16 flex-none">
                        <img
                          src={addon.thumbnail_url}
                          alt={addon.name}
                          className="w-full h-full object-cover rounded"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <h6 className="font-medium text-base leading-tight">
                        {addon.name}
                      </h6>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {addon.description}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-lg font-semibold text-primary">
                          R${' '}
                          {addon.price.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                        {discount > 0 && (
                          <>
                            <span className="text-sm text-muted-foreground line-through">
                              R${' '}
                              {addon.original_price.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                            <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">
                              -{discountPct}%
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div
                      className={`w-6 h-6 rounded border-2 flex items-center justify-center ${isSelected ? 'border-primary bg-primary' : 'border-gray-300'}`}
                    >
                      {isSelected && (
                        <svg
                          className="w-4 h-4 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </label>
              )
            })}
          </div>
        </div>
      )}

      {/* Resumo e CTA */}
      <div className="pt-6 border-t space-y-4">
        <div className="bg-secondary/10 border border-secondary/20 rounded-lg p-3 text-center">
          <p className="text-base font-semibold text-secondary">
            🚚 FRETE GRÁTIS para todo o Brasil!
          </p>
        </div>

        {selectedPageOption && (
          <div className="flex justify-between items-center text-base text-muted-foreground">
            <span>Detalhes:</span>
            <div className="text-right">
              <div className="font-semibold text-foreground">
                {selectedSubProduct && (
                  <>
                    {getOrientationLabel(selectedSubProduct.orientation)}{' '}
                    {selectedSubProduct.horizontal}x
                    {selectedSubProduct.vertical}cm
                  </>
                )}
              </div>
              <div className="text-sm">
                {selectedPageOption.numberPages} páginas (
                {selectedPageOption.maximumPhotos} fotos)
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold">Preço Total:</span>
          <span className="text-2xl font-bold text-primary">
            R${' '}
            {getTotalPrice().toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
            })}
          </span>
        </div>

        {selectedPageOption && (
          <div className="text-center">
            <p className="text-base text-muted-foreground">
              ou em até 12x de{' '}
              <span className="font-semibold text-foreground">
                R$ {(getTotalPrice() / 12).toFixed(2).replace('.', ',')}
              </span>
            </p>
          </div>
        )}

        <Button
          onClick={handleProceedToCheckout}
          className="w-full h-14 text-lg gap-3"
          size="lg"
          disabled={!selectedSubProduct || !selectedPageOption}
        >
          <ShoppingCart className="h-5 w-5" />
          Iniciar meu álbum
        </Button>
      </div>
    </div>
  )
}

export default ProductCustomizationInlineAPI
